import React, { useEffect, useRef } from "react";

const SPACING = 28;
const RADIUS = 180;

const readColors = () => {
  const styles = getComputedStyle(document.documentElement);
  return {
    dot: styles.getPropertyValue("--dot").trim(),
    accent: styles.getPropertyValue("--dot-accent").trim()
  };
};

// Ambient dot grid that swells and lights up around the cursor.
const DotField = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let colors = readColors();
    let width = 0;
    let height = 0;
    let frame = 0;
    let visible = true;
    const pointer = { x: -9999, y: -9999, tx: -9999, ty: -9999 };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (time) => {
      ctx.clearRect(0, 0, width, height);
      pointer.x += (pointer.tx - pointer.x) * 0.12;
      pointer.y += (pointer.ty - pointer.y) * 0.12;
      const t = time * 0.0006;

      for (let x = SPACING / 2; x < width; x += SPACING) {
        for (let y = SPACING / 2; y < height; y += SPACING) {
          const dx = x - pointer.x;
          const dy = y - pointer.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const near = Math.max(0, 1 - dist / RADIUS);
          const wave = reduceMotion ? 0 : Math.sin(t + x * 0.012 + y * 0.018) * 0.05;
          // Fade the field toward the bottom so content stays legible.
          const fade = 1 - Math.min(1, y / height) * 0.85;
          const alpha = (0.1 + wave + near * 0.75) * fade;
          const r = 0.9 + near * 1.6;
          const shift = near * 6;

          ctx.beginPath();
          ctx.fillStyle = `rgba(${near > 0.15 ? colors.accent : colors.dot}, ${Math.max(0, alpha)})`;
          ctx.arc(
            x + (dist ? (dx / dist) * shift : 0),
            y + (dist ? (dy / dist) * shift : 0),
            r,
            0,
            Math.PI * 2
          );
          ctx.fill();
        }
      }
    };

    const loop = (time) => {
      if (visible) draw(time);
      frame = requestAnimationFrame(loop);
    };

    const onPointer = (event) => {
      const rect = canvas.getBoundingClientRect();
      pointer.tx = event.clientX - rect.left;
      pointer.ty = event.clientY - rect.top;
    };

    const onLeave = () => {
      pointer.tx = -9999;
      pointer.ty = -9999;
    };

    resize();
    const resizeObserver = new ResizeObserver(() => {
      resize();
      if (reduceMotion) draw(0);
    });
    resizeObserver.observe(canvas);

    const themeObserver = new MutationObserver(() => {
      colors = readColors();
      if (reduceMotion) draw(0);
    });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

    const visibility = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
    });
    visibility.observe(canvas);

    if (reduceMotion) {
      draw(0);
    } else {
      frame = requestAnimationFrame(loop);
      window.addEventListener("pointermove", onPointer, { passive: true });
      document.addEventListener("pointerleave", onLeave);
    }

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      themeObserver.disconnect();
      visibility.disconnect();
      window.removeEventListener("pointermove", onPointer);
      document.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="hero__canvas" aria-hidden="true" />;
};

export default DotField;
